'use client';

import { type ReactNode, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layers, Users, BookOpen, PenLine, Search, X, Pickaxe, ChevronDown, ChevronUp, BookPlus, Lightbulb, GraduationCap } from 'lucide-react';
import { Z } from '@/lib/z-index';
import { SafeClawLogo } from '@/src/components/ui/safeclaw-logo';
import { NotificationCenter } from '@/src/components/community/notification-center';
import { UserMenu } from './user-menu';
import { LoginButton } from './login-button';
import { useAppContext } from '@/lib/app-context';
import { useAuthContext } from '@/lib/auth-context';
import { hasButtonPermission } from '@/lib/api/permissions';

/**
 * 临时、可撤销的上线配置：将对应项改回 true 即可恢复顶栏入口。
 * 入口功能和路由仍保留，避免后续恢复时重新实现。
 */
const TOP_BAR_VISIBILITY = {
	workplace: false,
	skillsMarket: false,
	community: false,
	search: false,
	creatorCenter: false,
} as const;

/**
 * TopBar — 全局固定顶栏（替代 FloatingNavPill）
 *
 * 布局：[Logo] [工作台] [技能市场] [学院] [社区]  ···弹性间距···  🔔 [发布?] 👤
 * 导航项恒定显示，不随 mode 变化。
 * 左右两端贴边，中间不用 max-width 限制。
 */
export function TopBar() {
	const navigate = useNavigate();
	const location = useLocation();
	const { mode, isDark, mounted, toggleTheme, goToSkills, goToCommunity, goToProfile, goToCourses, goToOrders } = useAppContext();
	const { isLoggedIn, logout, avatarUrl, currentUser, openLogin, permissionsReady } = useAuthContext();

	const isCommunity = mode === 'community';
	const isSkills = mode === 'skills';

	/* ── Create center menu state ── */
	const [createMenuOpen, setCreateMenuOpen] = useState(false);

	/* ── 按钮权限：控制创作菜单项可见性 ── */
	const canPublishSkill = useMemo(() => !permissionsReady || hasButtonPermission('btn:skill:create'), [permissionsReady]);
	const canCreateCourse = useMemo(() => !permissionsReady || hasButtonPermission('btn:course:create'), [permissionsReady]);

	/* ── Search state ── */
	const [searchValue, setSearchValue] = useState('');
	const [searchFocused, setSearchFocused] = useState(false);

	const handleSearchSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			const q = searchValue.trim();
			if (!q) return;
			// Dispatch custom event that community-landing-page listens to
			window.dispatchEvent(new CustomEvent('safeclaw:search', { detail: q }));
		},
		[searchValue],
	);

	const handleSearchClear = useCallback(() => {
		setSearchValue('');
		window.dispatchEvent(new CustomEvent('safeclaw:search', { detail: '' }));
	}, []);

	return (
		<header className="fixed top-0 left-0 right-0 h-14 bg-background/80 backdrop-blur-md border-b border-border/30" style={{ zIndex: Z.NAV_PILL }}>
			<div className="h-full flex items-center justify-between px-4 sm:px-6">
				{/* 左侧：Logo + 导航 */}
				<div className="flex items-center gap-1 sm:gap-2 shrink-0">
					{/* 临时、可撤销：Logo 禁止点击；恢复时重新传入 onClick 导航或刷新回调。 */}
					<SafeClawLogo className="shrink-0 mr-1" firework={false} height={28} />

					<nav className="flex items-center gap-0.5">
						{TOP_BAR_VISIBILITY.workplace && (
							<NavButton active={location.pathname === '/workplace'} icon={<Pickaxe className="h-4 w-4" />} label="工作台" onClick={() => navigate('/workplace')} />
						)}

						{TOP_BAR_VISIBILITY.skillsMarket && <NavButton active={mode === 'skills'} icon={<Layers className="h-4 w-4" />} label="技能市场" onClick={goToSkills} />}

						{TOP_BAR_VISIBILITY.community && <NavButton active={isCommunity} icon={<Users className="h-4 w-4" />} label="社区" onClick={goToCommunity} />}
						<NavButton active={mode === 'courses'} icon={<BookOpen className="h-4 w-4" />} label="学院" onClick={goToCourses} />
					</nav>
				</div>

				{/* 中间：搜索栏（技能市场页有自己的搜索框，此处隐藏） */}
				{TOP_BAR_VISIBILITY.search && !isSkills && (
					<form
						className={`hidden md:flex flex-1 max-w-md mx-4 items-center gap-2 h-8 rounded-full border px-3 transition-all duration-200 ${
							searchFocused ? 'border-primary/50 bg-secondary/60 shadow-sm shadow-primary/10' : 'border-border/40 bg-secondary/30 hover:bg-secondary/50'
						}`}
						onSubmit={handleSearchSubmit}
					>
						<Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
						<input
							className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none"
							onBlur={() => setSearchFocused(false)}
							onChange={(e) => setSearchValue(e.target.value)}
							onFocus={() => setSearchFocused(true)}
							placeholder="搜索能力、创作者、版块..."
							type="text"
							value={searchValue}
						/>
						{searchValue && (
							<button className="shrink-0 text-muted-foreground hover:text-foreground transition-colors" onClick={handleSearchClear} type="button">
								<X className="h-3 w-3" />
							</button>
						)}
					</form>
				)}

				{/* 右侧功能区 */}
				<div className="flex items-center gap-1.5 shrink-0">
					{/* 创作中心：主按钮跳转，箭头独立 hover 菜单 */}
					<div className={`relative h-8 items-center ${TOP_BAR_VISIBILITY.creatorCenter ? 'flex' : 'hidden'}`} onMouseLeave={() => setCreateMenuOpen(false)}>
						<button
							className="flex h-8 items-center gap-1.5 rounded-l-full bg-primary px-3 text-xs font-medium text-primary-foreground outline-none transition-colors hover:bg-primary/90 active:bg-primary/80 focus-visible:ring-2 focus-visible:ring-ring/50"
							onClick={() => {
								if (!isLoggedIn) {
									openLogin();
									return;
								}
								navigate('/creator-center');
							}}
							type="button"
						>
							<PenLine className="h-3.5 w-3.5" />
							<span className="hidden sm:inline">创作中心</span>
						</button>
						<button
							aria-label="创作快捷菜单"
							className="flex h-8 w-8 items-center justify-center rounded-r-full border-l border-primary-foreground/20 bg-primary text-primary-foreground outline-none transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring/50"
							onFocus={() => setCreateMenuOpen(true)}
							onMouseEnter={() => setCreateMenuOpen(true)}
							type="button"
						>
							{createMenuOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
						</button>
						{createMenuOpen && (
							<div className="absolute right-0 top-full w-[120px] pt-2">
								<div className="rounded-xl border border-border/40 bg-popover/95 p-1 shadow-lg backdrop-blur-md">
									<CreateMenuItem
										icon={<BookPlus className="h-3.5 w-3.5 text-muted-foreground" />}
										label="分享笔记"
										onClick={() => {
											if (!isLoggedIn) {
												openLogin();
												return;
											}
											window.dispatchEvent(new CustomEvent('safeclaw:open-publish'));
										}}
									/>
									{canPublishSkill && (
										<CreateMenuItem
											icon={<Lightbulb className="h-3.5 w-3.5 text-muted-foreground" />}
											label="发布技能"
											onClick={() => {
												if (!isLoggedIn) {
													openLogin();
													return;
												}
												window.dispatchEvent(new CustomEvent('safeclaw:open-skill-publish'));
											}}
										/>
									)}
									{canCreateCourse && (
										<CreateMenuItem
											icon={<GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />}
											label="创建课程"
											onClick={() => {
												if (!isLoggedIn) {
													openLogin();
													return;
												}
												window.dispatchEvent(new CustomEvent('safeclaw:open-course-create'));
											}}
										/>
									)}
								</div>
							</div>
						)}
					</div>
					{isLoggedIn && <NotificationCenter />}

					{/* 登录/用户菜单 */}
					{isLoggedIn ? (
						<UserMenu
							avatarUrl={avatarUrl ?? undefined}
							isDark={isDark}
							mounted={mounted}
							onGoToOrders={goToOrders}
							onGoToProfile={goToProfile}
							onLogout={logout}
							onToggleTheme={toggleTheme}
							username={currentUser?.username}
						/>
					) : (
						<LoginButton onClick={openLogin} />
					)}
				</div>
			</div>
		</header>
	);
}

function CreateMenuItem({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
	return (
		<button
			className="flex h-9 w-full items-center gap-2 rounded-lg px-3 text-left text-xs outline-none transition-colors hover:bg-secondary/60 focus-visible:bg-secondary/60"
			onClick={onClick}
			type="button"
		>
			{icon}
			<span>{label}</span>
		</button>
	);
}

function NavButton({ onClick, icon, label, active = false }: { onClick: () => void; icon: ReactNode; label: string; active?: boolean }) {
	return (
		<button
			className={`flex h-8 items-center gap-1.5 px-2.5 sm:px-3 rounded-full text-xs transition-colors whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
				active ? 'font-medium text-primary bg-primary/12 hover:bg-primary/18' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
			}`}
			onClick={onClick}
			type="button"
		>
			{icon}
			<span className="hidden sm:inline">{label}</span>
		</button>
	);
}
